import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { clientModelsService, ClientModelListItem } from "../../services/client-models.service";

const STATUS_LABELS: Record<string, { label: string; className: string }> = {
  draft: { label: "Borrador", className: "bg-yellow-100 text-yellow-700" },
  ready_for_quote: { label: "Listo para cotizar", className: "bg-green-100 text-green-700" },
  quoted: { label: "Cotizado", className: "bg-blue-100 text-blue-700" },
};

export function ModelsListPage() {
  const navigate = useNavigate();
  const [models, setModels] = useState<ClientModelListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await clientModelsService.list();
      setModels(data);
    } catch (err: any) {
      setError(err?.message || "Error al cargar modelos");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Mis modelos 3D</h2>
          <p className="text-sm text-slate-500 mt-1">
            Visualiza tus ideas con IA antes de cotizar
          </p>
        </div>
        <Link
          to="/dashboard/models/new"
          className="bg-primary hover:bg-primary-dark text-white px-4 py-2.5 rounded-lg font-semibold transition"
        >
          + Crear nuevo modelo
        </Link>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4">
          {error}
          <button onClick={load} className="ml-3 underline font-medium">Reintentar</button>
        </div>
      ) : models.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center">
          <div className="text-5xl mb-3 opacity-40">🪄</div>
          <h3 className="text-lg font-semibold text-slate-900">Aún no tienes modelos</h3>
          <p className="text-sm text-slate-500 mt-1 mb-5">
            Describe tu proyecto en texto y generaremos un modelo 3D automáticamente
          </p>
          <Link
            to="/dashboard/models/new"
            className="inline-block bg-primary hover:bg-primary-dark text-white px-5 py-2.5 rounded-lg font-semibold transition"
          >
            Crear mi primer modelo
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {models.map((model) => {
            const status = STATUS_LABELS[model.status] || STATUS_LABELS.draft;
            return (
              <button
                key={model.id}
                onClick={() => navigate(`/dashboard/models/${model.id}`)}
                className="text-left bg-white rounded-2xl border border-slate-200 hover:border-primary/40 hover:shadow-md transition p-5 flex flex-col gap-3"
              >
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-semibold text-slate-900 truncate">{model.name}</h3>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${status.className}`}>
                    {status.label}
                  </span>
                </div>
                {model.generationInput && (
                  <p className="text-sm text-slate-500 line-clamp-2 italic">
                    "{model.generationInput}"
                  </p>
                )}
                <div className="text-xs text-slate-400 mt-auto flex items-center justify-between">
                  <span>{new Date(model.createdAt).toLocaleDateString("es-MX")}</span>
                  <span className="bg-slate-100 px-2 py-0.5 rounded">
                    {model.parserUsed === "ai" ? "🤖 IA" : model.parserUsed === "hybrid" ? "🤖+⚡" : "⚡ Local"}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}